import { useState } from 'react';
import { View, TouchableOpacity, ScrollView, StyleSheet, Modal } from 'react-native';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { ArrowLeft, PawPrint, HelpCircle, X } from 'lucide-react-native';
import { Text } from '@/components';
import { useAuth } from '@/context/AuthContext';
import { useTrailDetail } from '@/lib/queries/trails';
import { useDogs } from '@/context/DogsContext';
import { useSelectedDog } from '@/context/SelectedDogContext';
import { useTranslation } from 'react-i18next';
import colors from '@/constants/colors';
import { Typography } from '@/constants/typography';
import LottieLoader from '@/components/LottieLoader';

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: colors.backgroundPrimary },
  content: { paddingHorizontal: 20, paddingBottom: 40 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 20 },
  backButton: {
    width: 44, height: 44, borderRadius: 22, backgroundColor: colors.backgroundSecondary,
    justifyContent: 'center', alignItems: 'center', borderWidth: 1, borderColor: colors.borderSubtle,
  },
  title: { ...Typography.h2(colors.accentPrimary) },
  subtitle: { ...Typography.body(colors.textMuted) },
  scoreCard: {
    backgroundColor: colors.backgroundSecondary, borderRadius: 18, borderWidth: 1,
    borderColor: colors.borderSubtle, padding: 22, alignItems: 'center', marginBottom: 18,
  },
  scoreValue: { ...Typography.h1(colors.accentPrimary) },
  scoreLabel: { ...Typography.caption(colors.textMuted), marginTop: 4 },
  factorCard: {
    backgroundColor: colors.backgroundSecondary, borderRadius: 18, borderWidth: 1,
    borderColor: colors.borderSubtle, padding: 18, marginBottom: 12,
  },
  factorHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: 12 },
  factorLabel: { ...Typography.label(colors.textPrimary), flexShrink: 1 },
  factorPenalty: { ...Typography.label(colors.textPrimary) },
  factorPenaltyMuted: { ...Typography.label(colors.textMuted) },
  baseRow: {
    flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center',
    paddingHorizontal: 6, marginBottom: 12,
  },
  baseLabel: { ...Typography.body(colors.textPrimary) },
  modalOverlay: { flex: 1, backgroundColor: 'rgba(0,0,0,0.5)', justifyContent: 'center', padding: 24 },
  modalCard: {
    backgroundColor: colors.backgroundPrimary, borderRadius: 18, borderWidth: 1,
    borderColor: colors.borderSubtle, padding: 20, maxHeight: '80%',
  },
  modalHeader: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 },
  modalTitle: { ...Typography.h3(colors.textPrimary), flexShrink: 1 },
  modalIntro: { ...Typography.caption(colors.textMuted), marginBottom: 14 },
  helpRow: { marginBottom: 12 },
  helpLabel: { ...Typography.label(colors.textPrimary) },
  helpText: { ...Typography.caption(colors.textMuted), marginTop: 2 },
});

const FACTOR_KEYS = ['dog_traffic', 'foot_traffic', 'exposure', 'exertion', 'off_leash', 'wildlife'] as const;

export default function TrailMatchScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { session } = useAuth();
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t } = useTranslation();
  const { dogId } = useSelectedDog();
  const { dogProfiles } = useDogs();
  const [helpVisible, setHelpVisible] = useState(false);
  // Same fallback as the API (`resolve_chosen_dog`: dogs[0] when no dogId) and DogPicker.
  const dog = (dogId ? dogProfiles.find((d) => d.id === dogId) : null) ?? dogProfiles[0] ?? null;

  const trailQuery = useTrailDetail(typeof id === 'string' ? id : undefined, session?.accessToken, dogId);
  const trail = trailQuery.data;
  // Server-computed: same score the trail lists show, itemized (no client-side
  // scoring logic — the formula lives only in tt-api/crud.py).
  const breakdown = trail?.dogMatchBreakdown ?? null;

  return (
    <View style={styles.container}>
      <Stack.Screen options={{ headerShown: false }} />
      <ScrollView contentContainerStyle={[styles.content, { paddingTop: insets.top + 12 }]}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={() => router.back()}>
            <ArrowLeft size={22} color={colors.accentPrimary} />
          </TouchableOpacity>
          <View style={{ flex: 1 }}>
            <Text style={styles.title}>{t('matchDetail.title')}</Text>
            {trail?.name ? <Text style={styles.subtitle} numberOfLines={1}>{trail.name}</Text> : null}
          </View>
          <TouchableOpacity style={styles.backButton} onPress={() => setHelpVisible(true)}>
            <HelpCircle size={22} color={colors.accentPrimary} />
          </TouchableOpacity>
        </View>

        {trailQuery.isLoading ? (
          <View style={{ paddingTop: 60, alignItems: 'center' }}><LottieLoader size={120} /></View>
        ) : !dog ? (
          <View style={styles.scoreCard}>
            <PawPrint size={32} color={colors.textMuted} />
            <Text style={[styles.subtitle, { textAlign: 'center', marginTop: 8 }]}>
              {t('matchDetail.noDog')}
            </Text>
          </View>
        ) : breakdown ? (
          <>
            <View style={styles.scoreCard}>
              <Text style={styles.scoreValue}>{`${Math.round(breakdown.score)}%`}</Text>
              <Text style={styles.scoreLabel}>{t('matchDetail.scoreFor', { dog: dog.name })}</Text>
              {breakdown.rawScore != null && (
                <Text style={[styles.scoreLabel, { marginTop: 4 }]}>
                  {t('matchDetail.capped', { dog: dog.name, raw: Math.round(breakdown.rawScore) })}
                </Text>
              )}
            </View>

            <View style={styles.baseRow}>
              <Text style={styles.baseLabel}>{t('matchDetail.baseScore')}</Text>
              <Text style={styles.baseLabel}>{t('matchDetail.points', { count: 45 })}</Text>
            </View>
            {breakdown.factors.map((f) => (
              <View key={f.key} style={styles.factorCard}>
                <View style={styles.factorHeader}>
                  <Text style={styles.factorLabel}>{t(`matchDetail.factors.${f.key}`)}</Text>
                  <Text style={f.penalty > 0 ? styles.factorPenalty : styles.factorPenaltyMuted}>
                    {f.penalty > 0 ? `−${f.penalty.toFixed(1)}` : f.penalty < 0 ? `+${(-f.penalty).toFixed(1)}` : '0.0'}
                  </Text>
                </View>
              </View>
            ))}
          </>
        ) : null}
      </ScrollView>

      <Modal visible={helpVisible} transparent animationType="fade" onRequestClose={() => setHelpVisible(false)}>
        <TouchableOpacity style={styles.modalOverlay} activeOpacity={1} onPress={() => setHelpVisible(false)}>
          <TouchableOpacity style={styles.modalCard} activeOpacity={1}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>{t('matchDetail.help.title')}</Text>
              <TouchableOpacity onPress={() => setHelpVisible(false)} hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}>
                <X size={20} color={colors.textMuted} />
              </TouchableOpacity>
            </View>
            <Text style={styles.modalIntro}>{t('matchDetail.help.intro')}</Text>
            <ScrollView showsVerticalScrollIndicator={false}>
              {FACTOR_KEYS.map((key) => (
                <View key={key} style={styles.helpRow}>
                  <Text style={styles.helpLabel}>{t(`matchDetail.factors.${key}`)}</Text>
                  <Text style={styles.helpText}>{t(`matchDetail.help.${key}`)}</Text>
                </View>
              ))}
            </ScrollView>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}